/**
 * Session orchestration — turns bridge calls into app state.
 *
 * One account is connected at a time. Its credentials are unsealed once per
 * connection and held in module memory only; every bridge request carries
 * them transparently (PROTOCOL.md §2). Folder and page loads paint from the
 * offline cache first, then reconcile with the server: an offline reload
 * shows the mail the device has already seen, with the failure note in
 * `listError` when the refresh could not reach the bridge.
 */

/* eslint-disable unicorn/no-top-level-assignment-in-function -- this module
   IS the session state machine: the module-level session slot and folder
   walk are its whole purpose, and assignments happen only through the
   exported lifecycle functions below. */

import {
  fetchFolders,
  fetchMessagePage,
  fetchMessagesByUids,
  fetchRawMessage,
  searchMailbox,
  sendMessage as sendViaBridge,
  updateFlags,
  SEARCH_SUMMARY_CAP,
} from "../bridge/api.ts";
import { BridgeError } from "../bridge/client.ts";
import { describeFailure } from "../bridge/failure.ts";
import type {
  Conn,
  FolderInfo,
  MessageSummary,
  SearchCriteria,
  SendRequest,
} from "../bridge/types.ts";
import { parseMessage } from "../engine/loader.ts";

import { listAccounts, revealCredentials } from "./accounts.ts";
import {
  cachedFolders,
  cachedPage,
  cachedRaw,
  dropAccountCache,
  dropMailboxCache,
  listQueued,
  queueSend,
  removeQueued,
  storeFolders,
  storePage,
  storeRaw,
  type QueuedSend,
} from "./cache.ts";
import {
  activeAccountId,
  activeFolderId,
  activeMessage,
  activeMessageId,
  connection,
  endOfMailbox,
  folderError,
  folders,
  foldersLoading,
  listError,
  listLoading,
  messageError,
  messageLoading,
  messages,
  queuedCount,
  resetFolderView,
  searchMode,
  selectAccount,
  sendError,
  sending,
} from "./store.ts";
import type { FolderSummary, MessageListItem } from "./store.ts";

/**
 * Live session: the connected account and its unsealed credentials.
 */
interface Session {
  /**
  Connected account id.
   */
  readonly accountId: string;
  /**
  Account email (the send envelope's `from`).
   */
  readonly email: string;
  /**
  IMAP credentials (port switched for SMTP sends).
   */
  readonly imap: Conn;
  /**
  SMTP credentials.
   */
  readonly smtp: Conn;
}

/**
 * The active session, `null` while nothing is connected.
 */
let session: Session | null = null;

/**
 * Summary rows for the connected folder, keyed by UID — the merge base for
 * paging and flag changes.
 */
const rows = new Map<number, MessageListItem>();

/**
 * Server flag sets per UID of the active folder (drives flag diffs).
 */
const flagSets = new Map<number, readonly string[]>();

/**
 * Wire summaries per UID of the active folder; the offline cache persists
 * these verbatim so a reload can re-parse the real header blocks.
 */
const wire = new Map<number, MessageSummary>();

/**
 * Folder walk state for the active folder.
 */
let walkState: {
  uidValidity: number;
  highestUid: number;
  lowestUid: number | null;
} | null = null;

/**
 * Connects an account: unseals credentials, then loads folders (cache
 * first). Failures surface in `folderError`; the session stays open so a
 * retry does not need the password again.
 *
 * @param accountId - Account id (lowercased email).
 */
export async function connectAccount(accountId: string): Promise<void> {
  session = null;
  rows.clear();
  flagSets.clear();
  wire.clear();
  walkState = null;
  const { profile, password } = await revealCredentials(accountId);
  session = {
    accountId,
    email: profile.email,
    imap: {
      host: profile.imapHost,
      port: profile.imapPort,
      username: profile.username,
      password,
    },
    smtp: {
      host: profile.smtpHost,
      port: profile.smtpPort,
      username: profile.username,
      password,
    },
  };
  const queued = await listQueued(accountId);
  queuedCount.value = queued.length;
  await loadFolders();
}

/**
 * Drops the session and every credential held in memory.
 */
export function disconnect(): void {
  session = null;
  rows.clear();
  flagSets.clear();
  wire.clear();
  walkState = null;
  connection.value = "no-account";
}

/**
 * Makes an account active: clears the previous account's view, then opens
 * the new one (cache first). Storage or connection failures surface in
 * `folderError` with the error state, so the retry path in the sidebar and
 * Settings both remain available.
 *
 * @param accountId - Account id (lowercased email) to activate.
 */
export async function activateAccount(accountId: string): Promise<void> {
  selectAccount(accountId);
  connection.value = "connected";
  try {
    await connectAccount(accountId);
  } catch (error: unknown) {
    connection.value = "error";
    folderError.value = describeFailure(
      error,
      "Could not open the account on this device.",
    );
  }
}

/**
 * Finishes an account removal: clears its offline cache, and when the
 * removed account was active, falls back to another saved account (or the
 * "no account" state).
 *
 * @param accountId - The id just removed from storage.
 */
export async function accountRemoved(accountId: string): Promise<void> {
  await dropAccountCache(accountId);
  if (activeAccountId.value !== accountId) {
    return;
  }
  const remaining = await listAccounts();
  const next = remaining[0];
  if (next === undefined) {
    activeAccountId.value = null;
    disconnect();
    return;
  }
  await activateAccount(next.id);
}

/**
 * True once an account is connected (credentials in memory).
 *
 * @returns Whether bridge calls can currently be attempted.
 */
export function isConnected(): boolean {
  return session !== null;
}

/**
 * Loads the folder list: paints the cache immediately, then reconciles with
 * the server.
 */
export async function loadFolders(): Promise<void> {
  if (session === null) {
    return;
  }
  const accountId = session.accountId;
  const cached = await cachedFolders(accountId);
  if (cached !== null) {
    folders.value = projectFolders(cached);
  }
  foldersLoading.value = true;
  folderError.value = null;
  try {
    const fresh = await fetchFolders(session.imap);
    await storeFolders(accountId, fresh);
    folders.value = projectFolders(fresh);
    connection.value = "connected";
  } catch (error: unknown) {
    if (cached === null) {
      connection.value = "error";
    }
    folderError.value = describeFailure(
      error,
      "Could not load the folder list.",
    );
  } finally {
    foldersLoading.value = false;
  }
}

/**
 * Loads the first page of a folder: paints the cache, then fetches the
 * newest page from the server.
 *
 * @param mailbox - Server mailbox name to open.
 */
export async function openFolder(mailbox: string): Promise<void> {
  if (session === null) {
    return;
  }
  activeFolderId.value = mailbox;
  resetFolderView();
  rows.clear();
  flagSets.clear();
  wire.clear();
  walkState = null;

  const cached = await cachedPage(session.accountId, mailbox);
  if (cached !== null) {
    for (const item of cached.items) {
      flagSets.set(item.uid, item.flags);
      wire.set(item.uid, item);
      rows.set(item.uid, toListItem(item));
    }
    walkState = {
      uidValidity: cached.uidValidity,
      highestUid: cached.highestUid,
      lowestUid: cached.items.at(-1)?.uid ?? null,
    };
    endOfMailbox.value = cached.complete;
    publishRows();
  }

  await fetchPageInto(mailbox, undefined);
}

/**
 * Fetches and merges one page of summaries into the active folder.
 *
 * @param mailbox - Server mailbox name.
 * @param uidFrom - Exclusive lower bound for the window, when paging.
 */
async function fetchPageInto(
  mailbox: string,
  uidFrom: number | undefined,
): Promise<void> {
  if (session === null || activeFolderId.value !== mailbox) {
    return;
  }
  listLoading.value = true;
  listError.value = null;
  try {
    const page = await fetchMessagePage(session.imap, mailbox, uidFrom);
    if (walkState !== null && walkState.uidValidity !== page.uidValidity) {
      // UIDs changed meaning; every cached row for this mailbox is void.
      await dropMailboxCache(session.accountId, mailbox);
      rows.clear();
      flagSets.clear();
      wire.clear();
    }
    walkState = {
      uidValidity: page.uidValidity,
      highestUid: page.highestUid,
      lowestUid: page.messages.at(-1)?.uid ?? walkState?.lowestUid ?? null,
    };
    for (const summary of page.messages) {
      flagSets.set(summary.uid, summary.flags);
      wire.set(summary.uid, summary);
      rows.set(summary.uid, toListItem(summary));
    }
    endOfMailbox.value =
      page.messages.length === 0 ||
      (walkState.lowestUid !== null && walkState.lowestUid <= 1);
    publishRows();
    // Persist the merged view (cached rows included), not just the fetched
    // page, so an offline reload shows everything the user currently sees.
    await persistPage();
    connection.value = "connected";
  } catch (error: unknown) {
    if (rows.size === 0) {
      connection.value = "error";
    }
    listError.value = describeFailure(error, "Could not load messages.");
  } finally {
    listLoading.value = false;
  }
}

/**
 * Fetches the next-older page of the active folder.
 */
export async function loadOlder(): Promise<void> {
  const mailbox = activeFolderId.value;
  if (session === null || mailbox === null || searchMode.value) {
    return;
  }
  const lowest = walkState?.lowestUid;
  if (lowest === null || lowest === undefined || lowest <= 1) {
    endOfMailbox.value = true;
    return;
  }
  await fetchPageInto(mailbox, lowest);
}

/**
 * Opens a message: parses cached raw bytes when present, otherwise fetches
 * and caches the full message. Unread messages are marked seen.
 *
 * @param uid - Message UID within the active folder.
 */
export async function openMessage(uid: number): Promise<void> {
  const mailbox = activeFolderId.value;
  if (session === null || mailbox === null) {
    return;
  }
  activeMessageId.value = uid;
  activeMessage.value = null;
  messageError.value = null;
  messageLoading.value = true;
  try {
    const cached = await cachedRaw(session.accountId, mailbox, uid);
    const raw =
      cached ?? (await storeFreshRaw(session.accountId, mailbox, uid));
    activeMessage.value = parseMessage(new Uint8Array(raw), true);
    connection.value = "connected";
    if (!(flagSets.get(uid) ?? []).includes(String.raw`\Seen`)) {
      await changeFlags(mailbox, uid, [String.raw`\Seen`], [], null);
    }
  } catch (error: unknown) {
    messageError.value = describeFailure(error, "Could not open the message.");
  } finally {
    messageLoading.value = false;
  }
}

/**
 * Toggles a flag on one message (`\Flagged` today).
 *
 * @param uid - Message UID.
 * @param flag - The IMAP flag to toggle.
 * @param shouldSet - Whether the flag should end up present.
 */
export async function toggleFlag(
  uid: number,
  flag: string,
  shouldSet: boolean,
): Promise<void> {
  const mailbox = activeFolderId.value;
  if (session === null || mailbox === null) {
    return;
  }
  await changeFlags(
    mailbox,
    uid,
    shouldSet ? [flag] : [],
    shouldSet ? [] : [flag],
    "Could not update the flag.",
  );
}

/**
 * Applies a flag change with optimistic update, then server verification.
 * A transport failure while marking seen is tolerated silently (the read
 * state stays local); other failures revert and surface a note.
 *
 * @param mailbox - Server mailbox name.
 * @param uid - Message UID.
 * @param add - Flags to add.
 * @param remove - Flags to remove.
 * @param failureNote - Copy for a hard failure; `null` to stay silent.
 */
async function changeFlags(
  mailbox: string,
  uid: number,
  add: readonly string[],
  remove: readonly string[],
  failureNote: string | null,
): Promise<void> {
  if (session === null) {
    return;
  }
  const previous = flagSets.get(uid) ?? [];
  const optimistic = [
    ...previous.filter((entry) => !remove.includes(entry)),
    ...add.filter((entry) => !previous.includes(entry)),
  ];
  applyFlags(uid, optimistic);
  try {
    const serverFlags = await updateFlags(
      session.imap,
      mailbox,
      uid,
      add,
      remove,
    );
    flagSets.set(uid, serverFlags);
    applyFlags(uid, serverFlags);
    await persistPage();
  } catch (error: unknown) {
    // Revert the optimistic change; a silent case (mark-seen while
    // offline) keeps the peace, anything else gets its note.
    flagSets.set(uid, previous);
    applyFlags(uid, previous);
    if (failureNote === null) {
      return;
    }
    if (error instanceof BridgeError && error.isTransient) {
      return;
    }
    listError.value = describeFailure(error, failureNote);
  }
}

/**
 * Runs a structured search over the active folder and shows the results,
 * newest first, capped to keep the UID literal bounded.
 *
 * @param criteria - The search criteria.
 */
export async function runSearch(criteria: SearchCriteria): Promise<void> {
  const mailbox = activeFolderId.value;
  if (session === null || mailbox === null) {
    return;
  }
  listLoading.value = true;
  listError.value = null;
  try {
    const uids = await searchMailbox(session.imap, mailbox, criteria);
    const newestFirst = uids.toReversed().slice(0, SEARCH_SUMMARY_CAP);
    rows.clear();
    flagSets.clear();
    wire.clear();
    if (newestFirst.length > 0) {
      const page = await fetchMessagesByUids(
        session.imap,
        mailbox,
        newestFirst,
      );
      for (const summary of page.messages) {
        flagSets.set(summary.uid, summary.flags);
        wire.set(summary.uid, summary);
        rows.set(summary.uid, toListItem(summary));
      }
    }
    searchMode.value = true;
    endOfMailbox.value = true;
    publishRows();
  } catch (error: unknown) {
    listError.value = describeFailure(error, "The search failed.");
  } finally {
    listLoading.value = false;
  }
}

/**
 * Leaves search mode and reloads the folder's rows.
 */
export function clearSearch(): void {
  if (activeFolderId.value === null) {
    return;
  }
  void openFolder(activeFolderId.value);
}

/**
 * Sends a message from the connected account. Transport-level failures
 * (offline, bridge unreachable) queue the message in the outbox; rejections
 * from the mail host throw with user copy for the composer to show.
 *
 * @param request - The outgoing message (no `from`; the account supplies it).
 * @returns `"sent"` when the server accepted recipients, `"queued"` when
 *   the message is parked in the outbox.
 * @throws {Error} With user-facing copy on a hard rejection.
 */
export async function sendMail(
  request: Omit<SendRequest, "from">,
): Promise<"sent" | "queued"> {
  if (session === null) {
    throw new Error("Connect an account before sending.");
  }
  const envelope: SendRequest = { ...request, from: session.email };
  sending.value = true;
  sendError.value = null;
  try {
    const receipt = await sendViaBridge(session.smtp, envelope);
    const queued = await listQueued(session.accountId);
    queuedCount.value = queued.length;
    return receipt.accepted.length > 0 ? "sent" : "queued";
  } catch (error: unknown) {
    if (error instanceof BridgeError && error.isTransient) {
      await queueSend(session.accountId, envelope);
      const queued = await listQueued(session.accountId);
      queuedCount.value = queued.length;
      return "queued";
    }
    throw new Error(describeFailure(error, "The message could not be sent."), {
      cause: error,
    });
  } finally {
    sending.value = false;
  }
}

/**
 * Delivers queued messages, oldest first, stopping at the first failure so
 * order is preserved. Called after a successful send and when the browser
 * reports the connection is back.
 */
export async function deliverOutbox(): Promise<void> {
  if (session === null) {
    return;
  }
  const queued = await listQueued(session.accountId);
  for (const entry of queued) {
    try {
      await sendViaBridge(session.smtp, entry.request);
      await removeQueued(entry.key ?? -1);
      const remaining = await listQueued(session.accountId);
      queuedCount.value = remaining.length;
    } catch {
      // Still unreachable: leave the queue untouched for the next attempt.
      return;
    }
  }
}

/**
 * Reads the outbox for display purposes (a `QueuedSend` carries no secret).
 *
 * @returns The queued messages for the connected account.
 */
export function readOutbox(): Promise<readonly QueuedSend[]> {
  return session === null ? Promise.resolve([]) : listQueued(session.accountId);
}

/**
 * Builds reply headers from the message being answered.
 *
 * @param uid - UID of the message being replied to.
 * @returns `inReplyTo`/`references` for the send request.
 */
export function replyHeadersFor(uid: number): {
  inReplyTo?: string;
  references?: string[];
} {
  const message = activeMessage.value;
  if (message === null || activeMessageId.value !== uid) {
    return {};
  }
  const references = [
    ...message.references,
    ...(message.message_id === null ? [] : [message.message_id]),
  ];
  return {
    ...(message.message_id !== null && { inReplyTo: message.message_id }),
    ...(references.length > 0 && { references }),
  };
}

// --- internals -------------------------------------------------------------

/**
 * Fetches a message's raw bytes and caches them before returning.
 *
 * @param accountId - Account id.
 * @param mailbox - Server mailbox name.
 * @param uid - Message UID.
 * @returns The freshly fetched raw bytes.
 */
async function storeFreshRaw(
  accountId: string,
  mailbox: string,
  uid: number,
): Promise<ArrayBuffer> {
  if (session === null) {
    throw new Error("No connected account.");
  }
  const raw = await fetchRawMessage(session.imap, mailbox, uid);
  await storeRaw(accountId, mailbox, uid, raw);
  return raw;
}

/**
 * Projects wire folders into sidebar summaries (`INBOX` first, then name).
 *
 * @param info - Wire folder listing.
 * @returns Sidebar summaries.
 */
function projectFolders(info: readonly FolderInfo[]): readonly FolderSummary[] {
  return info
    .map((folder) => ({
      id: folder.name,
      name: folder.name,
      unread: folder.unseen ?? 0,
    }))
    .toSorted((left, right) => {
      if (left.id === "INBOX") {
        return -1;
      }
      if (right.id === "INBOX") {
        return 1;
      }
      return left.name.localeCompare(right.name);
    });
}

/**
 * Converts a wire summary into a list row, parsing the header block with
 * the WASM engine for the display fields.
 *
 * @param summary - The wire summary.
 * @returns The list row.
 */
function toListItem(summary: MessageSummary): MessageListItem {
  const parsed = parseHeaderBlock(summary.headerBytes);
  const from = parsed?.from[0];
  return {
    id: summary.uid,
    from:
      from === undefined
        ? "(unknown sender)"
        : (from.display_name ?? from.address),
    subject: parsed?.subject ?? null,
    date: parsed?.date ?? summary.internalDate,
    unread: !summary.flags.includes(String.raw`\Seen`),
    flagged: summary.flags.includes(String.raw`\Flagged`),
    size: summary.size,
  };
}

/**
 * Parses a base64 header block; an unparseable block yields `null` so the
 * row can still render with honest, empty display fields.
 *
 * @param headerBytes - Base64 text of the RFC 5322 header block.
 * @returns The parsed message, or `null` when the engine rejects it.
 */
function parseHeaderBlock(
  headerBytes: string,
): ReturnType<typeof parseMessage> | null {
  try {
    return parseMessage(decodeBase64(headerBytes), true);
  } catch {
    return null;
  }
}

/**
 * Decodes standard base64 into bytes.
 *
 * @param text - Base64 text.
 * @returns The decoded bytes.
 */
function decodeBase64(text: string): Uint8Array {
  const binary = atob(text);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    // eslint-disable-next-line unicorn/prefer-code-point -- byte-level decode of atob output
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

/**
 * Publishes the merged rows to the list signal, newest first.
 */
function publishRows(): void {
  // `Iterator#toArray` (which unicorn prefers here) needs the ES2024 lib
  // and a 2024+ browser floor; the spread keeps the ES2023 target.
  messages.value =
    // eslint-disable-next-line unicorn/prefer-iterator-to-array -- see above
    [...rows.values()].toSorted((left, right) => right.id - left.id);
}

/**
 * Recomputes one row from the flag set and publishes.
 *
 * @param uid - The row's UID.
 * @param flags - The flags to render.
 */
function applyFlags(uid: number, flags: readonly string[]): void {
  const row = rows.get(uid);
  if (row === undefined) {
    return;
  }
  rows.set(uid, {
    ...row,
    unread: !flags.includes(String.raw`\Seen`),
    flagged: flags.includes(String.raw`\Flagged`),
  });
  publishRows();
}

/**
 * Rewrites the page cache from current state, preserving the wire summaries
 * (with their real header blocks) for the next offline start.
 */
async function persistPage(): Promise<void> {
  if (session === null || walkState === null || activeFolderId.value === null) {
    return;
  }
  // eslint-disable-next-line unicorn/prefer-iterator-to-array -- ES2023 target
  const items = [...wire.values()]
    .filter((summary) => rows.has(summary.uid))
    .map((summary) => ({
      ...summary,
      flags: flagSets.get(summary.uid) ?? summary.flags,
    }))
    .toSorted((left, right) => left.uid - right.uid);
  await storePage(session.accountId, activeFolderId.value, {
    uidValidity: walkState.uidValidity,
    highestUid: walkState.highestUid,
    items,
    complete: endOfMailbox.value,
  });
}
